import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNewDeviceFields } from 'modules/intelligent-layer/hooks';
import { BasicFieldsForm } from 'modules/intelligent-layer/forms/IntelligenceFieldsForm';
import { useTranslation } from 'utils/translation';
import { useModal } from '../../../../components/v2/Modal/useModal';
import { useModalContent } from '../../../../components/v2/Modal/useModalContent';
import { createModalFormFooter } from '../../../../components/v2/Modal/createModalFormFooter';
import Spacer from '../../../../components/v2/Spacer/Spacer';
import { mapValuesToPayload } from '../../../../modules/intelligent-layer/utils';
import { useCreateNewDevice } from '../hooks';
import Section from 'components/v2/Section/Section';
import Typography from 'components/v2/Typography/Typography';
import { Stack } from 'components/v2/Stack/Stack';
import Button, { ButtonGroup } from 'components/v2/Button/Button';
import { applyFilters } from '../../../../core';
import Filters from '../../../../core/filters';
import { useQuery } from '@tanstack/react-query';
import { refetchActiveQueries } from '../../../../utils/QueryClientProvider';
import http from '../../../../utils/http';
import AccountQuotaGuard from '../../../../modules/users/components/AccountQuotaGuard';
import repeat from '../../../../utils/repeat';
import Skeleton from '@mui/material/Skeleton';

const defaultRequiredFields = ['serial_number', 'device_type'];

const NEW_DEVICE_FORM_ID = 'plugin-device-new-device';

const NewDeviceModalFooter = createModalFormFooter(NEW_DEVICE_FORM_ID, {
  submitKey: 'Create new device',
});

function NewDeviceSuccessModalBody() {
  const { t } = useTranslation();

  return (
    <Section padding="small">
      <Stack direction="column" align="center">
        <Typography variant="body1" align="center">
          {t('new_device_success_body')}
        </Typography>
      </Stack>
    </Section>
  );
}

type Props = {
  onClose(): void;
  onSuccess?(): void;
  defaultType?: { label: string; value: number } | null;
};

const NewDeviceForm = ({ onClose, onSuccess, defaultType = null }: Props) => {
  const mutation = useCreateNewDevice();
  const [selectedType, setSelectedType] = useState<any | null>(defaultType);
  const modalContent = useModalContent();
  const formKey = selectedType?.value || 'unknown';
  const { data = [], isLoading } = useNewDeviceFields(selectedType);

  useEffect(() => {
    if (mutation.isError) {
      modalContent.scrollToTop();
    }
  }, [mutation.isError, modalContent]);

  const defaultValuesQuery = useQuery({
    queryFn: async () => {
      let defaultValues = {};

      const id = selectedType?.value ?? '';

      try {
        defaultValues = await http.get(`/api/device/default-values/${id}`);
      } catch (error) {
        console.error(error);
      }

      const custom = await (async () =>
        applyFilters(
          Filters.device.newDeviceDefaultValues,
          {
            device_type: selectedType,
          },
          {
            deviceType: selectedType,
          },
        ))();

      return {
        ...defaultValues,
        ...custom,
      };
    },
    queryKey: ['new_device_form_default_values', formKey],
    refetchInterval: 0,
    gcTime: 0,
  });

  const requiredFields = applyFilters(
    Filters.device.newDeviceRequiredFields,
    defaultRequiredFields,
  );

  const fields = useMemo(() => {
    return data.map((item) => {
      if (requiredFields.includes(item.name)) {
        item.settings.required = true;
      }

      if (
        item.type === 'select' &&
        Number(item.settings.options?.length || 0) <= 1
      ) {
        item.settings.readOnly = true;
      }

      return item;
    });
  }, [data]);

  const defaultValuesFromFields = useMemo(() => {
    return fields.reduce(
      (prev, item) => {
        if (item.type === 'select' && item.settings.options?.length === 1) {
          prev[item.name] = item.settings.options[0];
        }

        return prev;
      },
      {} as Record<string, any>,
    );
  }, [fields]);

  const handleChange = useCallback((values: any) => {
    if (values.hasOwnProperty('device_type')) {
      setSelectedType(values['device_type']);
    }
  }, []);

  const onSubmit = async (values: any) => {
    const payload = mapValuesToPayload(values);

    try {
      await mutation.mutateAsync(payload);
      void refetchActiveQueries();
      onSuccess?.();
    } catch (error: any) {
      return Promise.reject({
        response: error?.response,
      });
    }
  };

  if (isLoading || defaultValuesQuery.isLoading) {
    return (
      <Section padding="small">
        {repeat(7, (i) => (
          <React.Fragment key={i}>
            <Skeleton height={16} width="200px" />
            <Skeleton height={64} />
            <Spacer size={2} />
          </React.Fragment>
        ))}
      </Section>
    );
  }

  return (
    <BasicFieldsForm
      enableSuggestedValues
      key={formKey}
      defaultValues={{
        ...defaultValuesFromFields,
        ...defaultValuesQuery.data,
      }}
      onChange={handleChange}
      fields={fields}
      persister={onSubmit}
      formId={NEW_DEVICE_FORM_ID}
      renderButtons={false}
    />
  );
};

export const useCreateNewDeviceForm = () => {
  const modal = useModal();
  const { t } = useTranslation();

  return (
    props: Omit<React.ComponentProps<typeof NewDeviceForm>, 'onClose'> = {},
  ) => {
    function openDeviceFormModal() {
      modal.open(
        ({ close }) => (
          <AccountQuotaGuard quota="maxAssets">
            <NewDeviceForm
              {...props}
              onClose={close}
              onSuccess={() => {
                props.onSuccess?.();
                queueMicrotask(() => {
                  close();
                  openDeviceSuccessModal();
                });
              }}
            />
          </AccountQuotaGuard>
        ),
        {
          title: t('Create new device'),
          disableBackdropClose: true,
          footer: NewDeviceModalFooter,
        },
      );
    }

    function openDeviceSuccessModal() {
      modal.open(() => <NewDeviceSuccessModalBody />, {
        title: t('Device has been created'),
        footer: ({ close }) => (
          <ButtonGroup align="right">
            <Button
              variant="text"
              size="small"
              onClick={() => {
                close();
                queueMicrotask(() => openDeviceFormModal());
              }}
            >
              {t('Create next device')}
            </Button>
            <Button size="small" onClick={close}>
              {t('Close')}
            </Button>
          </ButtonGroup>
        ),
      });
    }

    openDeviceFormModal();
  };
};

export default NewDeviceForm;
